import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FormulaModeloStruct } from '@shared/services/structs/formula/formula-modelo.struct';
import { VariavelStruct } from '@shared/services/structs/formula/variavel.struct';
import { FormulaExpressaoSectionComponent } from '../formula-expressao-section/formula-expressao-section.component';
import { FormulaCardModoEnum } from '@shared/enums/motor-fiscal/formula-card-modo.enum';
import { descreverVigencia, VigenciaInfo } from '../utils/formula-modelo.util';
import { FORMULA_BADGE_CLASSES } from '../utils/formula-badge.util';

/**
 * Réplica selecionável do formula-card, usada na seção de troca de modelo.
 * Exibe um único modelo (não a fórmula inteira) e emite a seleção ao ser clicado.
 * `mostrarVigencia` exibe o header de vigência (usado na listagem; oculto no editor).
 */
@Component({
    selector: 'app-formula-modelo-card',
    standalone: true,
    imports: [CommonModule, MatButtonModule, MatIconModule, MatTooltipModule, FormulaExpressaoSectionComponent],
    templateUrl: './formula-modelo-card.component.html',
    styleUrl: './formula-modelo-card.component.css',
})
export class FormulaModeloCardComponent implements OnChanges {
    public readonly badgeClasses = FORMULA_BADGE_CLASSES;
    @Input({ required: true }) modelo!: FormulaModeloStruct;
    @Input() versoes: FormulaModeloStruct[] = [];
    @Input() rotuloModelo = '';
    /** Variáveis da fórmula — usadas para resolver os nomes na expressão. */
    @Input() variaveis: VariavelStruct[] = [];
    @Input() selecionado = false;
    @Input() mostrarVigencia = false;
    @Input() modo: FormulaCardModoEnum = FormulaCardModoEnum.Listagem;
    /** Editor: id do modelo padrão da empresa, para marcar o badge "Padrão". */
    @Input() idModeloPadrao: string | null = null;
    @Output() selecionar = new EventEmitter<void>();
    public indiceVersao = 0;
    private referenciaModeloInicial = '';

    public ngOnChanges(): void {
        const versoes = this.versoesOrdenadas;
        const referenciaModelo = this.referenciaVersao(this.modelo);
        if (referenciaModelo === this.referenciaModeloInicial) {
            if (this.indiceVersao >= versoes.length) {
                this.indiceVersao = Math.max(versoes.length - 1, 0);
            }
            return;
        }

        const indice = versoes.findIndex(item => item === this.modelo
            || item.dataVigenciaInicio === this.modelo?.dataVigenciaInicio && item.dataVigenciaFim === this.modelo?.dataVigenciaFim);
        this.indiceVersao = indice >= 0 ? indice : 0;
        this.referenciaModeloInicial = referenciaModelo;
    }

    public get versoesOrdenadas(): FormulaModeloStruct[] {
        return this.versoes.length > 0 ? this.versoes : [this.modelo];
    }


    public get modeloExibido(): FormulaModeloStruct {
        return this.versoesOrdenadas[this.indiceVersao] ?? this.modelo;
    }

    public get podeNavegarVersaoAnterior(): boolean {
        return this.indiceVersao > 0;
    }

    public get podeNavegarProximaVersao(): boolean {
        return this.indiceVersao < this.versoesOrdenadas.length - 1;
    }

    public navegarVersao(event: Event, deslocamento: number): void {
        event.stopPropagation();
        if (deslocamento < 0 && !this.podeNavegarVersaoAnterior) return;
        if (deslocamento > 0 && !this.podeNavegarProximaVersao) return;

        this.indiceVersao += deslocamento;
    }

    public get vigencia(): VigenciaInfo {
        return descreverVigencia(this.modeloExibido);
    }

    /**
     * Verdadeiro quando este card é o modelo "padrão".
     * - editor: padrão escolhido pela empresa (idModeloPadrao).
     * - listagem: padrão do sistema.
     */
    public get ehPadrao(): boolean {
        return this.modo === FormulaCardModoEnum.Editor
            ? this.modeloExibido.chaveFormulaModelo === this.idModeloPadrao
            : this.modeloExibido.isModeloPadraoSistema;
    }

    public get rotuloPadrao(): string {
        return this.modo === FormulaCardModoEnum.Editor ? 'Padrão da empresa' : 'Padrão do sistema';
    }

    public get classePadrao(): string {
        return this.modo === FormulaCardModoEnum.Editor
            ? this.badgeClasses.padraoEmpresa
            : this.badgeClasses.padraoSistema;
    }

    private referenciaVersao(modelo: FormulaModeloStruct): string {
        return `${modelo.chaveFormulaModelo}|${modelo.dataVigenciaInicio ?? ''}|${modelo.dataVigenciaFim ?? ''}`;
    }
}
